import { useCallback, useContext, useEffect } from 'react';

import { message } from 'antd';
import dayjs from 'dayjs';

import {
  AdditionalDataSection,
  ClientDataSection,
  PaymentMethodSection,
  QuotationStepButtons,
  ValiditySection,
} from '../../../../../shared';
import { TabSection } from './components';

import QuotationService from '../../../../../../../../../shared/services/quotation';

import { AuthContext } from '../../../../../../../../../shared/contexts';
import {
  GenericDataFormContext,
  QuotationInfoContext,
  StepOneFormContext,
} from '../../../../../shared/contexts';
import { StepFourFormContext } from '../../contexts/StepFourForm';

import { displayErrorMessage, formattedDateVigency, removeAccents } from '../../../../../../../../../shared/utils';

import {
  DECISION_TYPE,
  InsertInsuredPolicyholder,
  InsertVehicleDataServiceRequest,
  SendPaymentMethodRequest,
  SendProposalRequest,
  UserInformation,
} from '../../../../../../../../../shared/interfaces';
import { QuotationStep } from '../../../../../shared/interfaces';
import { StepTwoFormContext } from '../../../StepTwo/contexts/StepTwoForm';
import { WizardDataFormContext } from '../../../../Wizard/contexts/WizardDataForm';
import ProducersService from '../../../../../../../../../shared/services/producers';
import { UploadComplementaryDocumentRequest } from '../../../../../../../../../shared/interfaces/services/post-complementary-documents-service';
import userHelper from '../../../../../../../../../shared/utils/userHelper';
import { UploadFile } from 'antd/lib';
import { DocumentClass } from '../../../../../../../../../shared/interfaces/enums/document-class-enum';
import { QuotationContextData } from '../../../../../../../../../shared/interfaces/services/quote-pdf-service';
import EmailService, { buildSendMailRequest } from '../../../../../../../../../shared/services/email';
import { ContentTypeValue, MESSAGES_SENDMAIL } from '../../../../../../../../../shared/interfaces/services/email-services';
import { getQuoteDocumentBase64, transformToPdfQuote } from '../../../../../utils';
import { BranchDocumentsURL, BudgetType } from '../../../../../utils/quotation-documents.enum';
import { CompanyData } from '../../../../../../../../../shared/interfaces/services/company-data-enum';
import { INSPECTION_TYPES } from '../../../../interfaces';

export const FormIssue = () => {
  const { stepThreeForm } = useContext(WizardDataFormContext);
  const {
    additionalDataSectionForm,
    errorCreditCardMessage,
    listCreditCardInsured,
    paymentMethodFormValues,
    setAdditionalDataFormValues,
    setErrorCreditCardMessage,
    setMethodSelectedFromStepThree,
    setPaymentMethodFormValues,
    tabsSectionForm,
    validitySectionForm,
    inspectionList,
    useList,
  } = useContext(StepFourFormContext);
  const {
    clientDataSectionForm,
    paymentMethodSectionForm,
    setCanShowSpinner,
    setclientDataFormValues,
  } = useContext(GenericDataFormContext);
  const { clientSelected } = useContext(StepOneFormContext);
  const {
    setCanShowConfirmIssue,
    quotationInfo,
    setCurrentStep,
    setQuotationInfo,
  } = useContext(QuotationInfoContext);
  const authContext = useContext(AuthContext);
  const { tabs } = useContext(StepTwoFormContext);
  const { fileSelect, setFileSelect } = useContext(StepFourFormContext);

  const dateExpiredCreditCard = dayjs(paymentMethodFormValues?.expiration);

  const paymentMethodExpirationMonth = dateExpiredCreditCard?.month() + 1;
  const paymentMethodExpirationYear = dateExpiredCreditCard?.year();

  const numberCreditCard = String(paymentMethodFormValues?.cardNumber);

  const creditCardFiltered = listCreditCardInsured?.tarjetasDelAsegurado.find(
    (tarjeta: { numeroTarjeta: string }) =>
      tarjeta.numeroTarjeta === numberCreditCard
  );

  const sendClientData = useCallback(async () => {
    const {
      fullName,
      societyType,
      genderType,
      perceptionAgent,
      ivaCondition,
      address,
      documentNumber,
      documentType,
      cuit,
      nationality,
      inscriptionDate,
      inscriptionNumber,
      contractDate,
    } = clientDataSectionForm.getFieldsValue();

    const { cbuNumber, cardNumber, companyCreditCard } =
      paymentMethodSectionForm.getFieldsValue();

    const {
      profession,
      civilStatus,
      economicActivity,
      addressEmail,
      rutaNumber,
      cuilNumber,
      telephoneNumber,
      telephoneNumberJob,
      cellphoneNumber,
      webPage,
      birthDate,
      placeOfBirth,
      countryBirth,
    } = additionalDataSectionForm.getFieldsValue();

    const params: InsertInsuredPolicyholder = {
      numeroCotizacion: Number(quotationInfo!.token),
      codigoAsegurado: Number(clientSelected?.nroAsegurado),
      nombreApellido: fullName,
      tipoDeSociedad: societyType,
      profesion: profession,
      genero: genderType,
      estadoCivil: civilStatus,
      actividadEconomica: economicActivity,
      mail: addressEmail,
      agenteDePercepcion: perceptionAgent,
      cbuDePago: cbuNumber,
      cbuParaSiniestros: cbuNumber,
      nroDeRuta: rutaNumber,
      inscripcionIva: ivaCondition,
      domicilio: {
        calleYNro: address,
        codigoPostal: Number(clientSelected?.codigoPostal),
        sufijoPostal: Number(clientSelected?.sufijoCodigoPostal),
      },
      documento: {
        tipoDocumento: documentType,
        numeroDocumento: documentNumber,
        cuit: cuit,
        cuil: cuilNumber,
      },
      telefonos: {
        particular: telephoneNumber,
        laboral: telephoneNumberJob,
        celular: cellphoneNumber,
        paginaWeb: webPage,
      },
      nacimiento: {
        fecha: birthDate ? dayjs(birthDate).format('DD-MM-YYYY') : "01-01-1999",
        lugar: placeOfBirth || '',
        codigoPais: countryBirth || 0,
        codigoNacionalidad: nationality || 0,
      },
      inscripcion: {
        fecha: dayjs(inscriptionDate).format('DD-MM-YYYY'),
        numero: inscriptionNumber,
        fechaDeContrato: dayjs(contractDate).format('DD-MM-YYYY'),
      },
    };

    if (cardNumber) {
      params.tarjetaDeCredito = {
        idEmpresa: companyCreditCard,
        numero: cardNumber,
        anioFin: paymentMethodExpirationYear,
        mesFin: paymentMethodExpirationMonth,
      };
    }

    await QuotationService.postInsertInsuredPolicyholder(params);
  }, [
    clientDataSectionForm,
    additionalDataSectionForm,
    paymentMethodSectionForm,
    quotationInfo,
    clientSelected,
    paymentMethodExpirationMonth,
    paymentMethodExpirationYear,
  ]);

  const sendProposal = useCallback(async () => {
    const userName =
      authContext?.getUserInfo()?.intermediarioPrincipal?.nombreInter;

    const { startDate, endDate } = formattedDateVigency(
      validitySectionForm.getFieldValue('validityDate')
    );

    const params: SendProposalRequest = {
      numeroCotizacion: Number(quotationInfo!.token),
      fechaDesde: startDate,
      fechaHasta: endDate,
      usuario: userName,
    };

    try {
      const response = await QuotationService.postSendProposal(params);

      setQuotationInfo((prev) => ({
        ...prev!,
        proposalNumber: response.data.numeroDePropuesta,
      }));

      await invokeSendEmail();
      setCanShowConfirmIssue(true);
    } catch (error) {
      console.error(error);
    }
  }, [
    quotationInfo,
    authContext,
    setCanShowConfirmIssue,
    setQuotationInfo,
    validitySectionForm,
  ]);

  const sendPaymentMethod = useCallback(async () => {
    const { cbuNumber, cardNumber, companyCreditCard } =
      paymentMethodSectionForm.getFieldsValue();

    const codeMethodPayment = (
      stepThreeForm.getFieldValue('paymentMethod') as string
    ).split('-')[0];

    const creditCard = {
      idEmpresa: companyCreditCard ?? 0,
      numero: cardNumber || 0,
      anioFin: creditCardFiltered?.anioHasta ?? paymentMethodExpirationYear,
      mesFin: creditCardFiltered?.mesHasta ?? paymentMethodExpirationMonth,
    };

    const params: SendPaymentMethodRequest = {
      numeroCotizacion: Number(quotationInfo!.token),
      codigoFormaDePago: codeMethodPayment,
      numeroCbu: cbuNumber || 0,
      tarjetaDeCredito: creditCard,
    };

    try {
      await QuotationService.postInsertPaymentData(params);
    } catch (error: any) {
      paymentMethodSectionForm.setFields([{ name: 'cbuNumber', errors: [error.message] }]);
      throw error;
    }
  }, [
    paymentMethodSectionForm,
    creditCardFiltered,
    paymentMethodExpirationYear,
    quotationInfo,
    paymentMethodExpirationMonth,
    stepThreeForm,
  ]);

  const generateInsertVehicleForm = useCallback(
    (componentId: string): InsertVehicleDataServiceRequest => {
      const driverName =
        tabsSectionForm.getFieldValue(`driver-name-tab-${componentId}`) ?? '';

      const patent = tabsSectionForm.getFieldValue(`patent-tab-${componentId}`);

      const chassis = tabsSectionForm.getFieldValue(
        `chassisNumber-tab-${componentId}`
      );

      const engineNumber = tabsSectionForm.getFieldValue(
        `engineNumber-tab-${componentId}`
      );

      const ruta =
        tabsSectionForm.getFieldValue(`rutaNumber-tab-${componentId}`) ?? '0';

      const useCode = tabsSectionForm.getFieldValue(`use-tab-${componentId}`);

      const inspectionCode = tabsSectionForm.getFieldValue(
        `typeInspection-tab-${componentId}`
      );
      const inspectionType =
        inspectionList.find((el) => String(el.value) === String(inspectionCode))
          ?.label ?? '';

      const hasTracker =
        tabsSectionForm.getFieldValue(
          `trackers-hasTrackers-tab-${componentId}`
        ) === DECISION_TYPE.YES;

      const trackerCode = hasTracker
        ? tabsSectionForm.getFieldValue(
          `trackers-trackerBrand-tab-${componentId}`
        )
        : 0;

      const trackerContactPhone = !hasTracker
        ? tabsSectionForm.getFieldValue(`trackers-phone-tab-${componentId}`)
        : '';
      const trackerContactName = !hasTracker
        ? tabsSectionForm.getFieldValue(`trackers-contactName-tab-${componentId}`)
        : '';

      const trackerContactEmail = !hasTracker
        ? tabsSectionForm.getFieldValue(`trackers-email-tab-${componentId}`)
        : '';

      const hoursForContactForTrackers = tabsSectionForm.getFieldValue(
        `trackers-hoursForContact-tab-${componentId}`
      );

      const hourFromForTrackers = hoursForContactForTrackers?.length
        ? dayjs(hoursForContactForTrackers[0]).format('HH:mm')
        : undefined;
      const hourToForTrackers = hoursForContactForTrackers?.length
        ? dayjs(hoursForContactForTrackers[1]).format('HH:mm')
        : undefined;

      const hourFromFormattedForTrackers =
        hourFromForTrackers?.split(':').join('').concat('00') ?? '';
      const hourToFormattedForTrackers =
        hourToForTrackers?.split(':').join('').concat('00') ?? '';

      const creditorCode =
        tabsSectionForm.getFieldValue(`creditor-tab-${componentId}`) ?? '0';

      const fullNameContact =
        tabsSectionForm.getFieldValue(`fullname-contact-${componentId}`) ?? '';
      const phoneNumberContact =
        tabsSectionForm.getFieldValue(`phone-number-contact-${componentId}`) ??
        '';
      const emailContact =
        tabsSectionForm.getFieldValue(`email-contact-${componentId}`) ?? '';

      const hoursForContactForInspection = tabsSectionForm.getFieldValue(
        `hours-for-contact-${componentId}`
      );

      const hourFromForInspection = hoursForContactForInspection?.length
        ? dayjs(hoursForContactForInspection[0]).format('HH:mm')
        : undefined;
      const hourToForInspection = hoursForContactForInspection?.length
        ? dayjs(hoursForContactForInspection[1]).format('HH:mm')
        : undefined;

      const hourFromFormattedForInspection =
        hourFromForInspection?.split(':').join('').concat('00') ?? '';
      const hourToFormattedForInspection =
        hourToForInspection?.split(':').join('').concat('00') ?? '';

      const commentsForContact = tabsSectionForm.getFieldValue(
        `comments-contact-${componentId}`
      );

      const getValueCode = (useCode: any) => {
        // Si el valor recibido son solo dígitos (por ejemplo "1"), lo devolvemos tal cual
        if (/^\d+$/.test(String(useCode))) {
          return useCode;
        }
        // Si llega texto (ej: "PARTICULAR"), buscamos el código correspondiente
        const found = useList.find(
          (el) => String(el.label).toUpperCase() === String(useCode).toUpperCase()
        );
        return found?.value ?? '';
      };

      return {
        token: quotationInfo?.token!,
        rama: quotationInfo?.branch!,
        arse: quotationInfo?.arse!,
        componente: componentId,
        patente: patent,
        chasis: chassis,
        motor: engineNumber,
        tieneAverias: DECISION_TYPE.NO,
        nombreTarjetaCirculacion: driverName,
        codigoAcreedorPrendario: creditorCode,
        numeroRUTA: ruta,
        checkCesvi: DECISION_TYPE.YES,
        checkIris: DECISION_TYPE.YES,
        codigoUso: getValueCode(useCode),
        datosInspeccion: {
          tipo: removeAccents(inspectionType),
          nombreContacto: inspectionCode === INSPECTION_TYPES.TECNORED_CENTER ? fullNameContact : removeAccents(inspectionType),
          domicilio: '',
          numeroTelefono: phoneNumberContact,
          numeroCelular: phoneNumberContact,
          email: emailContact,
          horaDesde: hourFromFormattedForInspection,
          horaHasta: hourToFormattedForInspection,
          numeroInspeccionTecnored: '',
          centroTecnored: '',
          comentarios: commentsForContact,
          fechaInspeccion: '',
        },
        datosRastreador: {
          tiene: hasTracker ? DECISION_TYPE.YES : DECISION_TYPE.NO,
          codigo: trackerCode,
          descripcion: '',
          domicilio: '',
          numeroTelefono: trackerContactPhone ?? '',
          numeroCelular: trackerContactPhone ?? '',
          email: trackerContactEmail ?? '',
          horaDesde: hourFromFormattedForTrackers,
          horaHasta: hourToFormattedForTrackers,
          nombreContacto: trackerContactName,
        },
      };
    },
    [tabsSectionForm, quotationInfo, inspectionList]
  );

  const insertVehicles = useCallback(async () => {
    setCanShowSpinner(true);
    try {
      for (const { key } of tabs) {
        const form = generateInsertVehicleForm(key);
        await QuotationService.insertVehicleData(form);
      }
    } catch (error) {
      throw error;
    }
  }, [setCanShowSpinner, tabs, generateInsertVehicleForm]);

  const sendComplementaryDocuments = async (
    complementaryDocumentsFiles: UploadFile[]
  ): Promise<any[]> => {

    const {
      usuarioInterno,
    }: UserInformation = userHelper.getUser()!;

    const responses: any[] = [];

    for (let i = 0; i < complementaryDocumentsFiles.length; i++) {
      const file = complementaryDocumentsFiles[i];
      const fileToUpload = file as any;
      const name = file.name;
      const fileType = fileToUpload.mediaType || file.type;
      const base64 = fileToUpload.base?.split(',')[1] || '';
      const newFileName = "cotizacionAuto_documentacionComplementaria_" + quotationInfo?.token + "_ADJ_" + name;

      const payload: UploadComplementaryDocumentRequest = {
        uploadDocument: {
          documentClass: DocumentClass.Preclaims,
          fileName: newFileName,
          fileType,
          base64,
          applicationId: 'WebProductores',
          author: usuarioInterno,
          attributes: [
            { id: 'DocumentTitle', type: 'String', value: name },
            { id: 'cmis:name', type: 'String', value: name }
          ],
        },
      };

      try {
        const response = await ProducersService.postUploadComplementaryDocuments(
          payload
        );
        responses.push(response.data);
      } catch (error) {
      }
    }

    return responses;
  };

  const handlerOnClick = useCallback(async () => {
    setCanShowSpinner(true);

    try {
      await Promise.all([
        clientDataSectionForm.validateFields(),
        additionalDataSectionForm.validateFields(),
        paymentMethodSectionForm.validateFields(),
        tabsSectionForm.validateFields(),
      ]);

      setclientDataFormValues(clientDataSectionForm.getFieldsValue());
      setAdditionalDataFormValues(additionalDataSectionForm.getFieldsValue());
      setPaymentMethodFormValues(paymentMethodSectionForm.getFieldsValue());

      await sendComplementaryDocuments(fileSelect);

      await sendClientData();
      await sendPaymentMethod();
      await insertVehicles();

      await sendProposal();

    } catch (error: any) {
    } finally {
      setCanShowSpinner(false);
    }
  }, [
    additionalDataSectionForm,
    clientDataSectionForm,
    paymentMethodSectionForm,
    sendClientData,
    sendPaymentMethod,
    sendProposal,
    setAdditionalDataFormValues,
    setclientDataFormValues,
    setCanShowSpinner,
    setPaymentMethodFormValues,
    insertVehicles,
    tabsSectionForm,
    fileSelect
  ]);

  const handlerBackButtonOnClick = useCallback(() => {
    setclientDataFormValues(clientDataSectionForm?.getFieldsValue());
    setAdditionalDataFormValues(additionalDataSectionForm?.getFieldsValue());
    setPaymentMethodFormValues(paymentMethodSectionForm?.getFieldsValue());
    setCurrentStep(QuotationStep.THREE);
  }, [
    additionalDataSectionForm,
    clientDataSectionForm,
    paymentMethodSectionForm,
    setAdditionalDataFormValues,
    setclientDataFormValues,
    setCurrentStep,
    setPaymentMethodFormValues,
  ]);

  useEffect(() => {
    if (errorCreditCardMessage) {
      message.error(errorCreditCardMessage);
      setErrorCreditCardMessage('');
    }
  }, [errorCreditCardMessage, setErrorCreditCardMessage]);

  const { stepOneForm } = useContext(StepOneFormContext);
  const { provinceList, allLocalitiesPerProvince, ivaConditionList, paymentMethods, paymentMethodsList, allCoveragesPerPlan } = useContext(GenericDataFormContext);
  const { allArticleSelected, quoatationInfoRequest } = useContext(QuotationInfoContext);
  const { stepTwoForm, coverageSelectedByVehicle, quotationResponseByCar, reQuotationResponseByCar, quotationForm } = useContext(WizardDataFormContext);
  const { carFormValues, activeKey, brandsByYear, modelsByBrandAndYear, versionsByYearBrandAndModel, scoringQuestions } = useContext(StepTwoFormContext);

  const invokeSendEmail = async () => {
    const ctx: QuotationContextData = {
      stepOneForm, quotationForm,
      clientSelected, allArticleSelected,
      stepTwoForm, stepThreeForm, carFormValues,
      quotationInfo, quotationResponseByCar, reQuotationResponseByCar, quoatationInfoRequest,
      activeKey, coverageSelectedByVehicle,
      provinceList, allLocalitiesPerProvince, ivaConditionList, paymentMethods, paymentMethodsList, allCoveragesPerPlan,
      brandsByYear, modelsByBrandAndYear, versionsByYearBrandAndModel,
      scoringQuestions,
      branchDocumentsURL: BranchDocumentsURL.AUTOMOVILES
    };
    try {
      const distributionListKey = "PROPUESTA_" + String(ctx.quotationInfo?.article).padStart(6, '0');
      const result = await getQuoteDocumentBase64(transformToPdfQuote(ctx, BudgetType.PRODUCTOR), BranchDocumentsURL.AUTOMOVILES);

      if (result) {
        const payload = await buildSendMailRequest({
          subject: `Cotización ${ctx.quotationForm?.token} - ${CompanyData.COMPANY_NAME}`,
          body: 'Copia del Productor',
          includeAttachments: true,
          attachmentBase64: result.base64,
          attachmentName: result.fileName,
          attachmentMimeType: ContentTypeValue.PDF,
          saveToSentItems: true,
        }, distributionListKey);

        if (payload) await EmailService.sendMailViaLambda(payload);
      }
    } catch (error) {

      displayErrorMessage(MESSAGES_SENDMAIL.ERROR_MESSAGE);
    }
  }

  return (
    <>
      <ValiditySection validitySectionForm={validitySectionForm} />

      <ClientDataSection />

      <AdditionalDataSection />

      <PaymentMethodSection
        paymentMethodsForm={stepThreeForm}
        setMethodSelectedFromStepThree={setMethodSelectedFromStepThree}
      />

      <TabSection />

      <QuotationStepButtons
        label='Enviar'
        onClick={handlerOnClick}
        onPressBackButton={handlerBackButtonOnClick}
      />
    </>
  );
};
